import { useEffect, useState } from "react";
interface InstallEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: string }>;
}
export function usePwa() {
  const [online, setOnline] = useState(navigator.onLine),
    [offlineReady, setOfflineReady] = useState(false),
    [update, setUpdate] = useState<ServiceWorker | null>(null),
    [install, setInstall] = useState<InstallEvent | null>(null);
  useEffect(() => {
    const on = () => setOnline(navigator.onLine),
      prompt = (e: Event) => {
        e.preventDefault();
        setInstall(e as InstallEvent);
      };
    window.addEventListener("online", on);
    window.addEventListener("offline", on);
    window.addEventListener("beforeinstallprompt", prompt);
    if (import.meta.env.PROD && "serviceWorker" in navigator) {
      navigator.serviceWorker
        .register(`${import.meta.env.BASE_URL}sw.js`)
        .then((r) => {
          navigator.serviceWorker.ready.then(() => setOfflineReady(true));
          if (r.waiting) setUpdate(r.waiting);
          r.addEventListener("updatefound", () => {
            const worker = r.installing;
            worker?.addEventListener("statechange", () => {
              if (
                worker.state === "installed" &&
                navigator.serviceWorker.controller
              )
                setUpdate(worker);
            });
          });
        })
        .catch(() => setOfflineReady(false));
    }
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", on);
      window.removeEventListener("beforeinstallprompt", prompt);
    };
  }, []);
  const installApp = async () => {
    if (!install) return false;
    await install.prompt();
    await install.userChoice;
    setInstall(null);
    return true;
  };
  const updateApp = () => {
    if (update) {
      navigator.serviceWorker.addEventListener(
        "controllerchange",
        () => window.location.reload(),
        { once: true },
      );
      update.postMessage({ type: "SKIP_WAITING" });
    }
  };
  return {
    online,
    offlineReady,
    canInstall: !!install,
    installApp,
    update,
    updateApp,
  };
}
